// AGC: NoteMetadataStrip — shared icon strip for GPS, description + mic, and team tags
// AGC: Rendered at top-left of capture modals (overlay variant) or in card modals (card variant)

import React, { useState } from 'react'
import { MapPin, Pencil, Users, Mic, Loader2, X, Check, Send } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { formatCoordinates } from '../../utils/gpsUtils'
import type { GPSLocation } from '../../types/lotNotes'

interface NoteMetadataStripProps {
  // AGC: GPS
  gpsLocation?: GPSLocation
  gpsLoading: boolean
  gpsError: boolean
  onRetryGPS: () => void

  // AGC: Description
  description: string
  onDescriptionChange: (text: string) => void
  // AGC: Callback to save description (close panel)
  onDescriptionSave?: () => void

  // AGC: Speech-to-text
  isListening: boolean
  isProcessingSpeech?: boolean
  onToggleSpeechToText: () => void
  disableMic?: boolean

  // AGC: Team tags
  taggedUserIds: string[]
  onTaggedUsersChange: (ids: string[]) => void

  // AGC: Style variant
  variant?: 'overlay' | 'card'
}

/**
 * AGC: In-app Google Maps modal — shows map in iframe so user stays in the app
 */
const MapsModal: React.FC<{ lat: number; lng: number; onClose: () => void }> = ({ lat, lng, onClose }) => {
  const { t } = useTranslation()
  const mapUrl = `https://www.google.com/maps?q=${lat},${lng}&output=embed`

  return (
    <div className="fixed inset-0 z-[99999] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* AGC: Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {formatCoordinates(lat, lng)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {/* AGC: Map iframe */}
        <div className="flex-1 min-h-[300px]">
          <iframe
            src={mapUrl}
            className="w-full h-full min-h-[300px] border-0"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Maps"
          />
        </div>
        {/* AGC: Footer — open in full Google Maps */}
        <div className="p-3 border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={() => window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_system')}
            className="w-full py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            {t('notes.openInMaps')}
          </button>
        </div>
      </div>
    </div>
  )
}

export const NoteMetadataStrip: React.FC<NoteMetadataStripProps> = ({
  gpsLocation,
  gpsLoading,
  gpsError,
  onRetryGPS,
  description,
  onDescriptionChange,
  isListening,
  isProcessingSpeech = false,
  onToggleSpeechToText,
  disableMic = false,
  taggedUserIds,
  onTaggedUsersChange,
  variant = 'overlay',
}) => {
  const { t } = useTranslation()
  const [showDescription, setShowDescription] = useState(false)
  const [showTeamPicker, setShowTeamPicker] = useState(false)
  const [showMapsModal, setShowMapsModal] = useState(false)

  const isOverlay = variant === 'overlay'
  const iconBg = isOverlay ? 'bg-black/50 backdrop-blur-sm' : 'bg-gray-100 dark:bg-slate-700'
  const iconText = isOverlay ? 'text-white' : 'text-gray-700 dark:text-gray-300'

  return (
    <>
      <div className="absolute left-4 z-10 flex flex-col gap-3" style={{ top: 'max(8px, env(safe-area-inset-top))' }}>
        {/* AGC: Icon row — 2x bigger icons */}
        <div className="flex items-center gap-3">
          {/* AGC: GPS indicator */}
          <button
            onClick={() => {
              if (gpsLocation) {
                setShowMapsModal(true)
              } else if (gpsError) {
                onRetryGPS()
              }
            }}
            className={`p-3 rounded-xl ${iconBg} transition-all shadow-lg`}
            title={gpsLocation ? t('notes.openInMaps') : gpsError ? t('notes.gpsRetry') : 'GPS'}
          >
            {gpsLoading ? (
              <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
            ) : gpsError ? (
              <MapPin className="w-7 h-7 text-red-400" />
            ) : gpsLocation ? (
              <MapPin className="w-7 h-7 text-green-400" />
            ) : (
              <MapPin className={`w-7 h-7 ${iconText} opacity-50`} />
            )}
          </button>

          {/* AGC: Description toggle */}
          <button
            onClick={() => setShowDescription(!showDescription)}
            className={`p-3 rounded-xl ${iconBg} transition-all shadow-lg`}
            title={t('notes.addDescription')}
          >
            <Pencil className={`w-7 h-7 ${description.trim() ? 'text-green-400' : 'text-yellow-400'}`} />
          </button>

          {/* AGC: Team tag button */}
          <button
            onClick={() => setShowTeamPicker(!showTeamPicker)}
            className={`p-3 rounded-xl ${iconBg} transition-all shadow-lg relative`}
            title={t('notes.tagTeamMember')}
          >
            <Users className={`w-7 h-7 ${taggedUserIds.length > 0 ? 'text-green-400' : iconText} ${taggedUserIds.length === 0 ? 'opacity-50' : ''}`} />
            {taggedUserIds.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {taggedUserIds.length}
              </span>
            )}
          </button>
        </div>

        {/* AGC: Inline description panel — bigger textarea, save button, bigger mic */}
        {showDescription && (
          <div className={`rounded-2xl p-3 ${isOverlay ? 'bg-black/70 backdrop-blur-md' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 shadow-xl'}`} style={{ width: 'calc(100vw - 48px)', maxWidth: '340px' }}>
            <textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={t('notes.descriptionPlaceholder')}
              className={`w-full text-sm resize-none bg-transparent border-none outline-none ${
                isOverlay ? 'text-white placeholder:text-white/50' : 'text-gray-900 dark:text-white placeholder:text-gray-400'
              }`}
              rows={4}
              style={{ minHeight: '100px' }}
              autoFocus
            />
            {/* AGC: Action row — mic + save + close */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
              {/* AGC: Mic button — bigger */}
              <button
                onClick={onToggleSpeechToText}
                disabled={disableMic || isProcessingSpeech}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                  isProcessingSpeech
                    ? 'bg-blue-500 opacity-80 cursor-wait'
                    : isListening
                    ? 'bg-red-500'
                    : disableMic
                    ? 'opacity-30 cursor-not-allowed'
                    : isOverlay
                    ? 'bg-white/15 hover:bg-white/25'
                    : 'bg-gray-100 dark:bg-slate-700 hover:bg-gray-200'
                }`}
                title={isProcessingSpeech ? t('voice.processing') : isListening ? t('notes.tapToSend') : t('notes.tapToSpeak')}
              >
                {isProcessingSpeech ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : isListening ? (
                  <Send className="w-6 h-6 text-white" />
                ) : (
                  <Mic className={`w-6 h-6 ${isOverlay ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                )}
                <span className={`text-xs font-medium ${isProcessingSpeech || isListening ? 'text-white' : isOverlay ? 'text-white/70' : 'text-gray-500'}`}>
                  {isProcessingSpeech ? t('voice.processing') : isListening ? t('notes.tapToSend') : t('notes.tapToSpeak')}
                </span>
              </button>

              <div className="flex items-center gap-2">
                {/* AGC: Save description button */}
                <button
                  onClick={() => setShowDescription(false)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all ${
                    description.trim()
                      ? 'bg-green-500 hover:bg-green-600 text-white'
                      : isOverlay
                      ? 'bg-white/15 hover:bg-white/25 text-white/70'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-500'
                  }`}
                >
                  <Check className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AGC: Team picker placeholder */}
        {showTeamPicker && (
          <div className={`rounded-2xl p-3 ${isOverlay ? 'bg-black/70 backdrop-blur-md' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 shadow-xl'} max-w-[280px]`}>
            <p className={`text-xs mb-2 ${isOverlay ? 'text-white/60' : 'text-gray-400'}`}>
              {t('notes.tagTeamHint')}
            </p>
            <button
              onClick={() => setShowTeamPicker(false)}
              className={`text-xs px-3 py-1.5 rounded-lg ${isOverlay ? 'bg-white/15 text-white/70' : 'bg-gray-100 text-gray-500'}`}
            >
              {t('common.close')}
            </button>
          </div>
        )}
      </div>

      {/* AGC: Google Maps in-app modal */}
      {showMapsModal && gpsLocation && (
        <MapsModal
          lat={gpsLocation.latitude}
          lng={gpsLocation.longitude}
          onClose={() => setShowMapsModal(false)}
        />
      )}
    </>
  )
}

export default NoteMetadataStrip
